
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    
import style from '../../styles/content.module.css'

import { getLayout, LayoutContext } from '../../components/sg/Layout'
import Header from '../../components/Header'
import useTranslation from 'next-translate/useTranslation'
import classNames from 'classnames/bind'
import Link from 'next/link'
import Image from 'next/image'
import { useLivePrice } from '../../hooks/usePrices'
import resolveProductUnit from '../../helpers/resolveProductUnit'
import Cards from '../../components/sg/Cards'
import Footer from '../../components/sg/Footer'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useContext } from 'react'
import LangHeaders from '../../components/LangHeaders'

export default function SavingInGold () {
  const { t } = useTranslation('main')
  const c = classNames.bind(style)
  const router = useRouter()
  const { locale } = router
  const [layoutContext] = useContext(LayoutContext)
  const { currency } = layoutContext
  const { productPriceResolution } = resolveProductUnit('gram', 1)
  const { price: { value: spotPrice } } = useLivePrice(currency, 'gld', productPriceResolution)

  return (
    <>
      <Head>
        <title>投资者应投资购买黄金的五个原因 - S.T.A.R. Grams账号</title>
        <meta property='og:title' key='og:title' content='投资者应投资购买黄金的五个原因' />
        <meta property='og:url' key='og:url' content={router?.pathname} />
        <meta property='og:image' key='og:image' content='https://www.stargrams.app/images/og_starsavingsaccount-zh.png' />
        <meta property='og:description' key='og:description' content='黄金具备以上所涵盖的全部条件' />
        <meta property='og:type' key='og:type' content='article' />
        <meta property='og:site_name' key='og:site_name' content={t('layout-goldsaver')} />
        <meta name='title' key='title' content='投资者应投资购买黄金的五个原因' />
        <meta name='description' key='description' content='黄金具备以上所涵盖的全部条件' />
        <LangHeaders {...router} withExtension />
      </Head>
      <Header showLogo />
      <div className={c('content')}>
        <div className={c('hero')}>
          <Image src='/images/goldbars.jpg' layout='fill' alt='Photo of gold bars' />
        </div>
        <div className={c('inner')}>
          <div className={c('single')}>
            <h3>投资者应投资购买黄金的五个原因</h3>
            <h4>#1 – 黄金既是金钱</h4>
            <p>在历史上，黄金几乎存在于每一个文明国度里。 早在公元前 2700 年，它就在埃及被用作交换媒介。 公元前 550 年，吕底亚王国（现为现代土耳其的一部分）率先铸造金币。 黄金的价值不是通过国王或政府的法令得到认可，而是因为黄金自身具备了货币的特性。</p>
            <p>最好的货币形式通常具有以下六个特征:</p>
            <ul>
              <li>耐用——可多次重复使用且不会轻易损坏。</li>
              <li>便携——必须便于携带。</li>
              <li>统一——相同面额的所有版本必须具有相同的购买力。</li>
              <li>可分割– 可以分成更小的价值单位。</li>
              <li>可替代性– 一个单元可以与另一个单元互换。</li>
              <li>相对稀有且具有内在价值</li>
            </ul>
            <p>黄金具备以上所涵盖的全部条件。</p>
            <p>黄金不会失去光泽，不会生锈，是最耐腐蚀和抗氧化的金属。 它的耐用性说明了尽管金币、金饰被埋葬了几个世纪，但仍然毫发无损的被考古学家发掘、抛光并在博物馆中展示。</p>
            <p>鉴于黄金是地球上密度最高的金属之一，一块黄金在狭小的空间内具有相当大的价值，使其成为一种高度便携的价值储存手段。 按照目前的价格，比大多数智能手机尺寸略小，1 公斤金条的价值为  {(spotPrice * 1000 || 86000).toLocaleString(locale, { style: 'currency', currency: currency, currencyDisplay: 'code', maximumFractionDigits: 2, minimumFractionDigits: 2 })} 新加坡元。</p>
            <p>相同纯度、相同重量的黄金具有相同的购买力。 这种统一性使得黄金可以分割成更小的可互换的单位，使其成为一种极好的交换媒介。</p>
            <p>在历史当中，黄金作为货币也是全球央行继续持有黄金储备的原因。 当法定货币的价值出现争议或被破坏时，黄金一直是世界信任的终极后备。</p>

            <h4>#2 – 黄金是稀有有形资产</h4>
            <p>与法定货币不同，政府不能无限量印制黄金。 从地壳中开采黄金需要投入大量的资本和生产力。 黄金开采是风险最高的产业环节之一，因为从勘探、发现、矿山建设、提取和精炼的每个阶段都充满风险。 数千个黄金发现中只有一个能成为矿山，仅此过程就需要 15 年才能成功开矿。</p>
            <p>黄金通常与白银和铂金一同被称为贵金属。 之所以珍贵，是因为它在地壳中非常稀有。 黄金的地壳丰度仅为百万分之 0.004 (ppm)。 相比之下，地壳中的铜多出 15,000 倍，镍多出 21,000 倍——难怪黄金如此珍贵！</p>

            <h4>#3 – 黄金保护您的购买力</h4>
            <p>购买力是一单位货币可以购买的商品和服务的数量。 自 1913 年（即美联储成立的那一年）以来，作为世界储备货币的美元已经失去了 99% 以上的购买力。 因此，2020 年的 1 美元仅值 1913 年的 1 美分，平均每年损失 3.2%。 随着美联储无中生有地创造更多美元，每一美元的购买力将持续下降。</p>
            <p>从另一个角度看来，黄金则是一种极好的财富储存手段，可以随着时间的推移保持其价值。 黄金价格已从 1913 年的每金衡盎司 18.92 美元升至 2020 年的 2,000 美元以上，年增长率为 4.4%。</p>
            <p>纵观回首，历史时间线来到了1913 年，一盎司黄金也可以在罗马购置一件正装长袍，而今时今日这些金子仍然可以买一套漂亮的西装。 因此，黄金是抵御政府印钞的天然屏障，并且是每个投资组合中的重要资产。</p>

            <h4>#4 – 黄金是一种易于理解的低风险资产</h4>
            <p>黄金是当今最安全的资产之一。 实物黄金没有交易对手风险。 与法定货币或许多其他金融资产不同，金条不是价值承诺——它是价值本身的体现！ 您拥有的金条或金币不会出现破产或违约的情况。</p>
            <p>拥有黄金很简单，您不需要有专业知识。 在最简单的形式中，金条被购买、安全储存，并在需要出售时取出。 鉴于黄金是一种全球交易资产，它的价值是透明的，很容易在互联网和金银交易商的网站上找到。</p>

            <h4>#5 – 黄金是一种高流通性的硬资产</h4>
            <p>虽然股票、债券和房地产的价值有归零存在的风险，但黄金价格在人类历史上从未归零。 黄金作为货币，在经济繁荣或衰退时总是有需求的。 它具有高流通性，因为不乏黄金的买家或卖家。</p>
            <p>如果黄金持有者认为现行黄金价格有利，他们可以随时出售他们的黄金。 在这个数字化的时代，普遍黄金持有者会全天候在经销商处在线销售金条。 通过在线提交销售订单，确定销售价格通常是往往是透明的。 房地产等其他硬资产则不然，因为它们可能需要更长的时间才能找到买家。</p>
            <p>具有高流通性的黄金，可让您根据市场发展灵活的管理您的投资组合。</p>
            <br />
            <p>通过在下方注册，您可以开始使用我们的黄金储蓄账户来保护您的储蓄免受通货膨胀和不确定性的影响。</p>
          </div>
          <div className={c('promotion')}>
            {t('banner-mission-1')}<br />
            {t('banner-mission-2')}<br />
            <br />
            <br />
            <Link href='/signup'>
              <button className='goldenshimmer' title={t('banner-signup-button-title')}>{t('banner-signup-button')}</button>
            </Link>
            <span className={c('termsnote')}>{t('banner-signup-subtext')}</span>
          </div>
        </div>
      </div>
      <Cards />
      <Footer />
    </>
  )
}

SavingInGold.getLayout = getLayout


    export async function getStaticProps(ctx) {
        
        
        return {
          
          
          props: {
            
            ...(await __loadNamespaces({
              ...ctx,
              pathname: '/sg/why-should-i-save-in-gold-zh',
              loaderName: 'getStaticProps',
              ...__i18nConfig,
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
            }))
          }
        }
    }
  